import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AppConfig } from '@/config/app.config';
import { ApiConfig } from '@/config/api.config';
import { useAxiosAuth } from '@/hooks/useAxiosAuth';
import { useTranslation } from 'react-i18next';
import EToast from '@/components/everridge/EToast';
import {
	SeedTenantDemoDataRequestDto,
	TenantDemoSeedAvailabilityResponseDto,
} from '@/features/superAdmin/features/tenants/types/superAdmin-tenants-dto';

export const useTenantDemoSeedApi = (tenantId: string | undefined) => {
	const axiosInstance = useAxiosAuth();
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	const { data: availability, isLoading: isLoadingAvailability } = useQuery({
		queryKey: [ApiConfig.queries.superAdmin.tenants.queryKeys.demoSeedAvailability],
		queryFn: async (): Promise<TenantDemoSeedAvailabilityResponseDto> => {
			const url = ApiConfig.queries.superAdmin.tenants.endpoints.seedDemoDataAvailability;
			const response = await axiosInstance.get<TenantDemoSeedAvailabilityResponseDto>(url);
			return response.data;
		},
		enabled: AppConfig.features.showTenantDemoSeed && Boolean(tenantId),
		staleTime: 60_000,
	});

	const { mutateAsync: seedDemoData, isPending: isSeeding } = useMutation({
		mutationFn: async (dto: SeedTenantDemoDataRequestDto) => {
			const url = ApiConfig.queries.superAdmin.tenants.endpoints.seedDemoData.replace(
				'{id}',
				tenantId || '',
			);
			await axiosInstance.post(url, dto);
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: [ApiConfig.queries.superAdmin.tenants.queryKeys.list],
			});
			await queryClient.invalidateQueries({
				queryKey: [ApiConfig.queries.superAdmin.tenants.queryKeys.details, tenantId],
			});
			EToast({
				variant: 'success',
				message: t('main.superAdmin.tenants.components.details.demoSeed.successMessage'),
				description: t('main.superAdmin.tenants.components.details.demoSeed.successDescription'),
			});
		},
		onError: () => {
			EToast({
				variant: 'error',
				message: t('main.superAdmin.tenants.components.details.demoSeed.errorMessage'),
				description: t('main.superAdmin.tenants.components.details.demoSeed.errorDescription'),
			});
		},
	});

	return {
		demoSeedAvailable: Boolean(availability?.available),
		isLoadingAvailability,
		seedDemoData,
		isSeeding,
	};
};
